/**
 * Typed client for the CASES RECYCLE BIN gateway methods
 * (`cases-recycle-bin-lexide-handoff-2026-07-19.md` §3, contract v2).
 *
 * Case deletion is soft-delete only: `case.delete` retires the workspace to
 * `{orgRoot}/.case-recycle-bin/{slug}--{uid}--{ISO}` instead of removing it.
 * These three methods are the recovery surface over that bin.
 *
 * Two contract points shape everything here:
 *
 *   - **`folder` is the key, never `caseId`.** The bin can hold two
 *     retirements of the same case (retire → restore → retire), so the
 *     timestamped folder is the only unambiguous handle.
 *   - **Almost every display field is nullable.** A retired workspace whose
 *     `CASE_MODEL.json` is missing or corrupt still lists — with `caseId`,
 *     `title` and `slug` all null. The UI renders that as a fault and NEVER
 *     derives a name from the folder slug; that derivation habit is what hid
 *     the case-identity desync for a session.
 */
import { useGatewayStore } from "@/stores/useGatewayStore";
import { GatewayRpcError } from "@/lib/gateway-client";

/** Closed set of server error codes (handoff §3). Messages are already
 *  user-facing pt-BR and must be surfaced verbatim, not re-written. */
export type RecycleBinErrorCode =
  /** No such bin entry — also what a path-traversal handle receives. */
  | "NOT_FOUND"
  /** Restore target occupied by a live case; rename it first. */
  | "CONFLICT"
  /** Retired before restore receipts shipped — NOT auto-restorable, and the
   *  UI must not offer a retry (the original folder name is genuinely
   *  ambiguous: a `{slug}--{uid}` folder already contains `--`). */
  | "NO_RECEIPT"
  /** Not an org-admin — returned by purge AND (since the 2026-07-19 access
   *  ruling, `22cee041`) by RESTORE and the retire path too. Also covers an
   *  unresolvable identity or a failed admin lookup, since every gate fails
   *  CLOSED. The bin entry is left untouched, and the pt-BR message says what
   *  IS still possible — ask an org admin. */
  | "FORBIDDEN"
  /** Confirmation missing or not one of the accepted locale tokens. */
  | "CONFIRMATION_REQUIRED"
  | "INTERNAL";

export interface RecycleBinRetiredBy {
  userId: string | null;
  /** A real NAME when lexserver has one, otherwise the user's EMAIL,
   *  otherwise `null` (contract v2 + runtime `resolveDisplayName`). Never a
   *  fabricated name, and never a `runtime:{org}` service principal — those
   *  are filtered before any lookup. A failed lookup degrades to `null` and
   *  never breaks the listing, because the bin is a RECOVERY surface. */
  displayName: string | null;
}

export interface RecycleBinEntry {
  /** ALWAYS present — the handle every action keys on. */
  folder: string;
  /** From the retired `CASE_MODEL.json`; all three are null together when
   *  the model is missing or corrupt. */
  caseId: string | null;
  title: string | null;
  slug: string | null;
  /** ISO 8601 UTC, ms precision. Null only for a pre-receipt retirement. */
  retiredAt: string | null;
  retiredBy: RecycleBinRetiredBy;
}

export interface RecycleBinPage {
  items: RecycleBinEntry[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** Server default; max 500. Paging exists because an old bin would
 *  otherwise return everything on every call — the same unbounded-listing
 *  failure this pane's task poller shipped a fix for. */
export const RECYCLE_BIN_PAGE_SIZE = 100;

/**
 * The typed confirmation the destructive-case dialog requires, per locale.
 *
 * In v1 this gates the RETIRE (soft-delete) action — purge is blocked in all
 * affordances — so the name is intent-neutral rather than purge-flavoured.
 *
 * A locale SET rather than one English string: asking a Brazilian lawyer to
 * type "I UNDERSTAND" to authorise the only destructive action in the
 * product reads as a technicality instead of a warning, which defeats the
 * point of a typed gate. The server accepts ALL tokens regardless of the
 * caller's locale, so switching language can never lock an admin out of
 * their own bin.
 *
 * Matching is exact after trimming outer whitespace — paste artifacts are
 * forgiven, case is NOT folded, because deliberateness is the feature.
 */
export function confirmationTokenForLocale(locale: string | undefined): string {
  const lang = (locale ?? "pt").slice(0, 2).toLowerCase();
  if (lang === "en") return "I UNDERSTAND";
  if (lang === "es") return "LO ENTIENDO";
  return "EU ENTENDO";
}

/** True when the typed text matches the locale's token under the server's
 *  own rule (trim outer whitespace, do not fold case). Kept next to the
 *  token so the dialog cannot drift from what the server will accept. */
export function isConfirmationTokenValid(
  typed: string,
  locale: string | undefined,
): boolean {
  return typed.trim() === confirmationTokenForLocale(locale);
}

/**
 * NO PURGE CLIENT IN v1 — deliberately, and it must not be reintroduced
 * without a ruling.
 *
 * `cases.recycle_bin.purge` exists server-side and stays org-admin +
 * token gated, but the operator blocked the permanent destroy in ALL
 * affordances for v1 (2026-07-19). Shipping an unused wrapper for it would
 * leave a one-import path to irreversible data loss sitting in the client
 * for the next person who needs "a delete"; the bin holds retired cases and
 * nothing in the product destroys them.
 */

/** Narrow a thrown gateway error to its contract code, when it carries one. */
export function recycleBinErrorCode(err: unknown): RecycleBinErrorCode | undefined {
  if (err instanceof GatewayRpcError && typeof err.code === "string") {
    return err.code as RecycleBinErrorCode;
  }
  return undefined;
}

function rpc() {
  const client = useGatewayStore.getState().rpc;
  if (!client) {
    // Not a fallback: there is no meaningful degraded behaviour for a
    // recovery action, so refuse loudly rather than appear to succeed.
    throw new Error("gateway not connected");
  }
  return client;
}

export function listRecycleBin(
  params: { limit?: number; offset?: number } = {},
): Promise<RecycleBinPage> {
  return rpc().call<RecycleBinPage>("cases.recycle_bin.list", {
    limit: params.limit ?? RECYCLE_BIN_PAGE_SIZE,
    offset: params.offset ?? 0,
  });
}

/** Result of a disk action that also mirrors to the lexserver registry.
 *
 *  `registrySynced: false` means the DISK action succeeded but the registry
 *  mirror did not follow — the case is physically restored/purged while
 *  registry-backed lists may still disagree. That is drift the user must be
 *  told about: silently reporting plain success would leave them staring at a
 *  case list that contradicts what they just did. */
export interface RecycleBinActionResult {
  caseId?: string | null;
  registrySynced?: boolean;
}

export function restoreFromRecycleBin(
  folder: string,
): Promise<RecycleBinActionResult> {
  return rpc().call<RecycleBinActionResult>("cases.recycle_bin.restore", {
    folder,
  });
}

